import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import SidePanel from 'common/side-panel/side-panel';
import { Input } from 'antd';
import styled from 'styled-components';
import { Div, Flex, Button, NoteTitle, NoteName, NoteText, Icon } from 'ui';
import ErrorHandler from 'common/error/components/error-handler';
import { useSelector, useDispatch } from 'react-redux';
import { resetSaveNoteSuccess } from '../dashboard/containers/dashboard-actions';

export const Divider = styled(Div)`
  border-bottom: 1px solid ${p => p.theme.colors.border};
  margin: 0.5rem 0;
`;

const Buttons = styled(Flex)`
  > :first-child {
    margin-right: 5px;
  }
`;

const TextAreaWrapper = styled(Div)`
  margin: 0 2px;
  height: 100%;

  > textarea {
    height: 180px !important;
  }
`;

const LockedIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts[2]};
`;

const LockedText = styled.span`
  color: ${p => p.theme.colors.alerts[2]};
  font-weight: ${p => p.theme.fontWeight.bolder};
`;

const EditNotes = ({
  intl,
  notes,
  close,
  save,
  isLoading,
  error,
}) => {
  const dispatch = useDispatch();
  const [reducedNotes, setReducedNotes] = useState(() => notes.reduce((all, item) => {
    if (item.editable) {
      return {
        ...all,
        [item.id]: {
          text: item.noteTexts
            ?.filter(n => n.textLine !== null)
            .reduce((acc, curr) => `${acc}${acc ? '\n' : ''}${curr.textLine}`, ''),
          edited: false,
        },
      };
    }
    return all;
  }, {}));

  const saveNotesSuccess = useSelector(state => state.dashboard.saveNotesSuccess);

  useEffect(() => {
    if (saveNotesSuccess && !Object.keys(error).length) {
      dispatch(() => resetSaveNoteSuccess());
      close();
    }
  }, [saveNotesSuccess]);

  const editNoteText = ({ id, text }) => {
    setReducedNotes(prevNotes => ({
      ...prevNotes, [id]: { text, edited: true },
    }));
  };

  const saveNotes = () => {
    // Only return edited notes.
    const editedNotes = Object.keys(reducedNotes).reduce((all, item) => {
      const note = reducedNotes[item];

      if (note.edited) {
        const fullNote = notes?.find(n => n.id === item);
        const noteTexts = note.text.split('\n')?.map(l => ({ textFormat: '', textLine: l }));
        return [...all, { ...fullNote, noteTexts }];
      }
      return all;
    }, []);
    save(editedNotes);
  };

  return (
    <SidePanel
      width={'40%'}
      title={intl.formatMessage({ id: 'notes.TITLE' })}
      onPanelClose={close}
    >
      <ErrorHandler type='notification' error={error} />
      {Object.keys(reducedNotes)?.map((key) => {
        const note = reducedNotes[key];
        const fullNote = notes?.find(n => n.id === key);

        return (
          <Div key={key}>
            <Flex marginBottom={5}>
              <NoteTitle>
                <NoteName>
                  {intl.formatMessage({
                    id: fullNote?.noteType
                      ? `notes.${fullNote.noteType.toUpperCase()}`
                      : 'notes.NULL',
                  })}
                </NoteName>
                {fullNote?.isEntityLocked ?
                  <LockedText>
                    <LockedIcon type='exclamation-triangle' />{' '}
                    <FormattedMessage id='order.status.locked' />
                  </LockedText>
                  :
                  ''
                }
              </NoteTitle>
            </Flex>
            <NoteText>
              <TextAreaWrapper>
                <Input.TextArea
                  value={note.text}
                  onChange={e => editNoteText({ id: key, text: e.target.value })}
                  disabled={isLoading || fullNote?.isEntityLocked}
                />
              </TextAreaWrapper>
            </NoteText>
          </Div>
        );
      })}
      <Buttons>
        <Button onClick={close}>
          <FormattedMessage id='partners.CANCEL_BTN' />
        </Button>
        <Button
          type='secondary'
          onClick={saveNotes}
          loading={isLoading}
          disabled={!reducedNotes || !Object.values(reducedNotes)?.find(n => n.edited)}
        >
          <FormattedMessage id='ticket.BUTTON_SAVE' />
        </Button>
      </Buttons>
    </SidePanel>
  );
};

EditNotes.propTypes = {
  intl: PropTypes.object,
  notes: PropTypes.array,
  close: PropTypes.func,
  save: PropTypes.func,
  isLoading: PropTypes.bool,
  error: PropTypes.object,
};

export default injectIntl(EditNotes);
